import type { AppRouteRecordRaw } from '/@/router/types';
import { REDIRECT_NAME, LAYOUT2, PAGE_NOT_FOUND_NAME, PAGE_UPDATE_NAME } from '/@/router/constant';
import { t } from '@mcex/locale';
import { PageEnum } from '/@/enums/pageEnum';
import { Error, UpdatePage } from '/@/views/sys/exception';
import { useAppStoreWithOut } from '/@/store/modules/app';
import { isChinaMainland } from '/@/api/sys/system';
import { useProductStore } from '/@/store/modules/exchangeService/product';
import { OperationType, useTradEtpStoreWithOut } from '/@/store/modules/exchangeService/tradeEtp';
import { EtpDetailType } from '/@/api/exchangeService/model/productEtp';
import { DROTypeEnum } from '/@/enums/exchangeServiceEnum';
import { getFeatureStateByName } from '/@/utils/featureToggle';

// 404 on a page
export const PAGE_NOT_FOUND_ROUTE: AppRouteRecordRaw = {
  path: '/:path(.*)*',
  name: PAGE_NOT_FOUND_NAME,
  component: Error,
  meta: {
    title: 'ErrorPage',
    hideBreadcrumb: true,
    hideMenu: true,
  },
};

export const REDIRECT_ROUTE: AppRouteRecordRaw = {
  path: '/redirect',
  component: LAYOUT2,
  name: 'RedirectTo',
  meta: {
    title: REDIRECT_NAME,
    hideBreadcrumb: true,
    hideMenu: true,
  },
  children: [
    {
      path: '/redirect/:path(.*)',
      name: REDIRECT_NAME,
      component: () => import('/@/views/sys/redirect/index.vue'),
      meta: {
        title: REDIRECT_NAME,
        hideBreadcrumb: true,
      },
    },
  ],
};

export const HomeRoute: AppRouteRecordRaw = {
  path: '',
  name: 'HomeIndex',
  component: LAYOUT2,
  redirect: PageEnum.HOME, //  '/' 路由重定向 为 /home
  meta: {
    title: t('routes.basic.home'),
  },
  children: [
    {
      path: PageEnum.HOME,
      name: 'Home',
      component: () =>
        getFeatureStateByName('metf')
          ? import('/@/views/newHome/index.vue')
          : import('/@/views/newHome/oldIndex.vue'),
      meta: {
        title: t('routes.basic.home'),
      },
    },
  ],
};

export const PAGE_UPDATE_ROUTE: AppRouteRecordRaw = {
  path: '/sys_update',
  name: PAGE_UPDATE_NAME,
  component: UpdatePage,
  meta: {
    title: t('sys.app.sysMaintenance'),
    hideBreadcrumb: true,
    hideMenu: true,
  },
  beforeEnter: (_to, from) => {
    const appStore = useAppStoreWithOut();
    if (appStore.getMaintenance === false) {
      return { path: from.path };
    }
    return true;
  },
};

/**
 * 问券调查-机构
 */

export const QuestionnaireRoute: AppRouteRecordRaw = {
  path: '/questionnaire',
  name: 'Questionnaire',
  component: () => import('/@/views/question/institution/index.vue'),
  meta: {
    title: t('routes.basic.questionnaire'),
  },
};

/**
 * 问券调查-个人
 */

export const QuestionnaireIpiRoute: AppRouteRecordRaw = {
  path: '/questionnaire-ipi',
  name: 'QuestionnaireIpi',
  component: () => import('/@/views/question/individual/index.vue'),
  meta: {
    title: t('routes.basic.questionnaireIpi'),
  },
  beforeEnter: (_to, _from) => {
    // console.log('beforeEnter');
    // 跳转到信息介绍
    return { path: '/information' };
  },
};
/**
 * 问券调查-企业
 */

export const QuestionnaireCpiRoute: AppRouteRecordRaw = {
  path: '/questionnaire-cpi',
  name: 'QuestionnaireCpi',
  component: () => import('/@/views/question/company/index.vue'),
  meta: {
    title: t('routes.basic.questionnaireCpi'),
  },
  beforeEnter: (_to, _from) => {
    // console.log('beforeEnter');
    // 跳转到信息介绍
    return { path: '/information' };
  },
};

/**
 * 问券调查-信息介绍
 */
export const QuestionnaireInfoRoute: AppRouteRecordRaw = {
  path: '/information',
  name: 'QuestionnaireInfo',
  component: () => import('/@/views/question/info/index.vue'),
  meta: {
    title: t('routes.basic.questionnaireInfo'),
  },
  beforeEnter: async (_to, _form, next) => {
    // 获取当前域名
    const domain = window.location.host;
    // 线上域名不是brokerage.mcex.mo的时候，重定向到brokerage.mcex.mo/#/portal/information
    if (['mt.microconnect.cc'].includes(domain)) {
      window.location.replace('https://brokerage.mcex.mo/portal/#/information');
      return;
    }
    const res = await isChinaMainland();
    if (res) {
      next(PageEnum.FORBIDDEN_PAGE + '?status=10101');
    } else {
      next();
    }
  },
};

/** 产品列表 详情*/
export const ProductInfoRoute: AppRouteRecordRaw = {
  path: '/transactions',
  name: 'Transaction',
  component: LAYOUT2,
  redirect: PageEnum.TRANSACTION + '/:pType?',
  meta: {
    code: 'trading',
    orderNo: 2,
    hideChildrenInMenu: true,
    icon: 'menus-transaction|svg',
    title: t('routes.mcex.transaction'),
  },
  children: [
    {
      path: 'transaction/:pType?/:dType?/:brandCode?',
      props: true,
      name: 'Transaction',
      component: () => import('/@/views/exchangeService/transaction/index.vue'),
      meta: {
        title: t('routes.mcex.transaction'),
      },
      beforeEnter: (to, _from, next) => {
        const productType = to.params.pType;
        // productType 为 :productType时 修复地址移除:pType
        if (productType === ':pType') {
          next({ path: PageEnum.TRANSACTION });
        } else {
          next();
        }
      },
    },
    {
      path: 'detail/:productCode/:viewType',
      name: 'detail',
      component: () => import('/@/views/exchangeService/detail/index.vue'),
      meta: {
        title: t('routes.mcex.detail'),
        currentActiveMenu: PageEnum.TRANSACTION,
      },
      beforeEnter: (to) => {
        const productStore = useProductStore();
        productStore.fetchProductData(to.params.productCode as string);
        return true;
      },
    },
    {
      path: 'buy',
      name: 'TransfromBuying',
      component: () => import('/@/views/exchangeService/buying/buy.vue'),
      meta: {
        title: t('routes.mcex.purchase'),
        hideMenu: true,
        currentActiveMenu: PageEnum.TRANSACTION,
      },
    },
    {
      path: 'detail_etp/:productCode/:pageType/:drpType?',
      name: 'DetailEtp',
      component: () => import('/@/views/exchangeService/detail_etp/index.vue'),
      meta: {
        title: t('routes.mcex.drpDetail'),
        currentActiveMenu: PageEnum.TRANSACTION,
      },
      beforeEnter: async (to) => {
        const tradEtpStore = useTradEtpStoreWithOut();
        tradEtpStore.setOperartType(to.params.pageType as OperationType);
        tradEtpStore.setEtpInfo({
          ...tradEtpStore.etpInfo,
          productCode: to.params.productCode as string,
        } as EtpDetailType);
        tradEtpStore.fetchEtpData(to.params.productCode as string, to.params.drpType as string);
        tradEtpStore.fetchProductExInfo(to.params.productCode as string, DROTypeEnum.IO);
        return true;
      },
      children: [
        {
          path: 'asset_detail',
          name: 'AssetDetail',
          component: () => import('/@/views/exchangeService/asset_detail/index.vue'),
          meta: {
            title: t('资产明细'),
            currentActiveMenu: PageEnum.TRANSACTION,
          },
        },
      ],
    },
    {
      path: 'dro_detail/:droCode/:listed?',
      name: 'droDetailL2',
      component: () => import('/@/views/market/dro/index.vue'),
      meta: {
        // affix: true,
        title: t('routes.mcex.droDetail'),
        hideBreadcrumb: false,
        currentActiveMenu: PageEnum.TRANSACTION,
      },
    },
    {
      path: 'allDro/:brandId?',
      props: true,
      name: 'tradingAllDroPage',
      component: () => import('/@/views/exchangeService/transaction/AllDro.vue'),
      meta: {
        // affix: true,
        title: t('maap.allDro'),
        hideBreadcrumb: false,
        currentActiveMenu: PageEnum.TRANSACTION,
      },
    },
  ],
};
